import classNames from 'classnames'
import { FC, ReactNode } from 'react'
import { useDropzone } from 'react-dropzone'
import styled from 'styled-components'
import { useTranslation } from 'next-i18next'

import {
  AUDIO_SCRIPT_MAX_DURATION,
  AUDIO_SCRIPT_MAX_SIZE,
} from '@/constants/tools/solid-background-video'
import { useToolsNotification } from '@/components/tools/common/hooks/notification'

const getAudioDurationFromFile = async (file: File | Blob): Promise<number> => {
  const src = URL.createObjectURL(file)

  try {
    return await new Promise<number>((resolve, reject) => {
      const audio = new Audio(src)

      audio.onloadedmetadata = () => {
        resolve(audio.duration)
      }

      audio.onerror = () => {
        reject(new Error('Failed to get audio duration'))
      }
    })
  } finally {
    URL.revokeObjectURL(src)
  }
}

export const UploadDropzone: FC<{
  onChangeAudioScriptFile: (audioScriptFile?: File) => void

  children?: ReactNode
  className?: string
}> = ({
  onChangeAudioScriptFile,

  children,
  className,
}) => {
  const { t } = useTranslation('common')

  const { open: openNotification, contextHolder } = useToolsNotification()

  const { getRootProps, getInputProps, isDragAccept, isDragReject } =
    useDropzone({
      onDrop: async (acceptedFiles, rejectedFiles) => {
        if (rejectedFiles.length > 0) {
          openNotification(
            t(
              'Invalid file format. Please check the file and try again later.',
            ),
          )

          return
        }

        const file = acceptedFiles[0]

        if (!file) {
          onChangeAudioScriptFile(file)
          return
        }

        const duration = await getAudioDurationFromFile(file)

        if (duration > AUDIO_SCRIPT_MAX_DURATION / 1000) {
          openNotification(
            t(
              'Invalid file format. Please check the file and try again later.',
            ),
          )

          return
        }

        onChangeAudioScriptFile(file)
      },
      accept: {
        'audio/mpeg': [],
        'audio/wav': [],
        'octet/stream': ['.mp3', '.wav'],
      },
      maxSize: AUDIO_SCRIPT_MAX_SIZE,
      maxFiles: 1,
    })

  return (
    <UploadDropzoneContainer
      {...getRootProps()}
      className={classNames(className, {
        accept: isDragAccept,
        reject: isDragReject,
      })}
    >
      {children}

      <input {...getInputProps()} />

      {contextHolder}
    </UploadDropzoneContainer>
  )
}

/**
 * &.accept
 * &.reject
 */
const UploadDropzoneContainer = styled.div`
  transition: border-color 0.3s;

  &.accept {
    border-color: #00e676;
  }

  &.reject {
    border-color: #e00751;
  }
`
